// landing-ads-params.js — recover Google Ads / campaign attribution for GA4
// when the visible URL no longer carries the click parameters.
//
// WHY: the SPA seeds history.state at mount (App.jsx popstate effect) and used
// to replaceState with pathname only, stripping ?gclid/utm before GA4 (which
// loads only after analytics consent) ever sent its first collect hit — every
// Ads session was classified direct/organic. The replaceState now keeps the
// query (part C), but a user can still NAVIGATE before accepting the consent
// banner: the [page] pushState carries no query, so by the time initAnalytics
// runs the URL is clean again. Part D (this lib) rebuilds a page_location
// override from the LANDING URL of the CURRENT page load.
//
// MISATTRIBUTION GUARD (the dangerous part): the override derives ONLY from
// the landing search string captured at App.jsx module eval — i.e. what the
// real address bar contained when THIS document loaded, before any strip.
// This lib never reads localStorage/gh_attribution, so a click ID persisted
// days ago can never be replayed onto a fresh organic session: no ads params
// in this load's landing URL → no override, period.

// Click IDs + campaign params GA4/Ads read from page_location. gbraid/wbraid
// are the consent-era iOS variants of gclid; fbclid keeps Meta parity with
// captureAttribution. Everything else (?payment, ?ref, …) must never leak in.
export const ADS_PARAM_KEYS = [
  'gclid', 'gbraid', 'wbraid', 'fbclid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
];

// Extract the ads-relevant params from a search string ("?a=1" or "a=1").
// Returns a plain object of the present keys, or null when none are present.
export function extractAdsParams(search) {
  if (typeof search !== 'string' || !search) return null;
  let params;
  try { params = new URLSearchParams(search); } catch { return null; }
  const out = {};
  let any = false;
  for (const key of ADS_PARAM_KEYS) {
    const v = params.get(key);
    if (v) { out[key] = v; any = true; }
  }
  return any ? out : null;
}

// Build the page_location override for gtag('config', GA_ID, {...}).
// Returns null when no override is needed or possible:
//   - the landing search carried no ads params (organic / stale-storage case),
//   - the current URL already contains every landing ads param (part C kept
//     the query intact — gtag reads the real URL, overriding would be noise),
//   - the current href does not parse.
// Otherwise returns the CURRENT href with the missing landing ads params
// merged into its query (existing query and #hash preserved) — the user is
// attributed to the click that opened this page load, on the page they are
// actually on when consent lands.
export function adsPageLocationOverride(landingSearch, currentHref) {
  const ads = extractAdsParams(landingSearch);
  if (!ads) return null;
  let url;
  try { url = new URL(currentHref); } catch { return null; }
  let added = false;
  for (const key of Object.keys(ads)) {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, ads[key]);
      added = true;
    }
  }
  return added ? url.toString() : null;
}
